/**
 * PostHog Analytics Module
 *
 * Behavioral usage tracking for NAUD app.
 * Designed to answer:
 * - Whether users reliably complete digital checklists
 * - Whether usage is consistent over time
 * - Whether offline-first usage is reliable
 * - Whether managers actively use the system
 */

import posthog from "posthog-js";

export enum AnalyticsEvent {
  // Session events
  SESSION_STARTED = "session_started",
  SESSION_ENDED = "session_ended",
  // Checklist events
  CHECKLIST_LIST_VIEWED = "checklist_list_viewed",
  CHECKLIST_STARTED = "checklist_started",
  CHECKLIST_COMPLETED = "checklist_completed",
  CHECKLIST_MARKED_MISSED = "checklist_marked_missed",
  CHECKLIST_REVIEWED = "checklist_reviewed",
  // Evidence events
  EVIDENCE_ADDED = "evidence_added",
  // Offline/sync events
  OFFLINE_MODE_ENTERED = "offline_mode_entered",
  SYNC_COMPLETED = "sync_completed",
  SYNC_FAILED = "sync_failed",
}

// App version from build - uses Vercel commit SHA or git commit
const APP_VERSION =
  import.meta.env.VITE_VERCEL_GIT_COMMIT_SHA?.slice(0, 7) ||
  import.meta.env.VITE_COMMIT_SHA?.slice(0, 7) ||
  "dev";

// Role mapping for analytics (simplified from internal role types)
type AnalyticsRole = "manager" | "deputy" | "leader" | "practitioner";

function mapRole(
  userRoleType: string | null | undefined,
): AnalyticsRole | "unknown" {
  switch (userRoleType) {
    case "NURSERY_MANAGER":
    case "ORG_ADMIN":
    case "PLATFORM_ADMIN":
      return "manager";
    case "NURSERY_DEPUTY_MANAGER":
      return "deputy";
    case "NURSERY_LEADER":
      return "leader";
    case "NURSERY_PRACTITIONER":
      return "practitioner";
    default:
      return "unknown";
  }
}

// Device type detection
type DeviceType = "desktop" | "tablet" | "mobile";

function getDeviceType(): DeviceType {
  if (typeof window === "undefined") return "desktop";

  const ua = navigator.userAgent.toLowerCase();
  const width = window.innerWidth;

  // Check for mobile user agents
  const isMobile =
    /android|webos|iphone|ipod|blackberry|iemobile|opera mini/i.test(ua);
  const isTablet = /ipad|tablet|playbook|silk/i.test(ua);

  if (isTablet || (width >= 768 && width < 1024 && isMobile)) {
    return "tablet";
  }
  if (isMobile || width < 768) {
    return "mobile";
  }
  return "desktop";
}

// Shared properties context (set once, used for all events)
interface AnalyticsContext {
  userId: string | null;
  role: AnalyticsRole | "unknown";
  nurseryId: string | null;
  isSharedDevice: boolean;
}

let analyticsContext: AnalyticsContext = {
  userId: null,
  role: "unknown",
  nurseryId: null,
  isSharedDevice: false,
};

/**
 * Set the analytics context (call after login and when nursery changes)
 */
export function setAnalyticsContext(context: {
  userId: string | null;
  userRoleType: string | null;
  nurseryId: string | null;
}): void {
  const role = mapRole(context.userRoleType);
  analyticsContext = {
    userId: context.userId,
    role,
    nurseryId: context.nurseryId,
    // Shared device = practitioner on mobile/tablet
    isSharedDevice: role === "practitioner" && getDeviceType() !== "desktop",
  };
}

/**
 * Clear analytics context (call on logout)
 */
export function clearAnalyticsContext(): void {
  analyticsContext = {
    userId: null,
    role: "unknown",
    nurseryId: null,
    isSharedDevice: false,
  };
}

// Get shared properties for all events
function getSharedProperties(): Record<string, unknown> {
  return {
    user_id: analyticsContext.userId,
    role: analyticsContext.role,
    nursery_id: analyticsContext.nurseryId,
    device_type: getDeviceType(),
    is_shared_device: analyticsContext.isSharedDevice,
    is_offline: !navigator.onLine,
    app_version: APP_VERSION,
    timestamp: new Date().toISOString(),
  };
}

/**
 * Capture an analytics event with shared properties
 */
function capture(
  eventName: string,
  properties: Record<string, unknown> = {},
): void {
  const allProperties = {
    ...getSharedProperties(),
    ...properties,
  };

  posthog.capture(eventName, allProperties);
}

// ============================================
// SESSION EVENTS
// ============================================

/**
 * Track session start (after successful login)
 */
export function trackSessionStarted(
  loginMethod: "email_password" | "username_password" | "pin",
): void {
  capture(AnalyticsEvent.SESSION_STARTED, {
    login_method: loginMethod,
  });
}

/**
 * Track session end (logout or auto-logout)
 */
export function trackSessionEnded(
  logoutReason: "manual" | "inactivity_timeout",
  sessionDurationSeconds: number,
): void {
  capture(AnalyticsEvent.SESSION_ENDED, {
    logout_reason: logoutReason,
    session_duration_seconds: sessionDurationSeconds,
  });
}

// ============================================
// CHECKLIST EVENTS
// ============================================

/**
 * Track checklist list view
 */
export function trackChecklistListViewed(counts: {
  dueTodayCount: number;
  overdueCount: number;
  upcomingCount: number;
}): void {
  capture(AnalyticsEvent.CHECKLIST_LIST_VIEWED, {
    due_today_count: counts.dueTodayCount,
    overdue_count: counts.overdueCount,
    upcoming_count: counts.upcomingCount,
  });
}

/**
 * Track checklist started
 */
export function trackChecklistStarted(params: {
  checklistInstanceId: string;
  checklistTemplateId: string;
  isOverdue: boolean;
}): void {
  capture(AnalyticsEvent.CHECKLIST_STARTED, {
    checklist_instance_id: params.checklistInstanceId,
    checklist_template_id: params.checklistTemplateId,
    is_overdue: params.isOverdue,
  });
}

/**
 * Track checklist completed
 */
export function trackChecklistCompleted(params: {
  checklistInstanceId: string;
  checklistTemplateId: string;
  completedOnTime: boolean;
  completionDurationSeconds: number;
  completedOffline: boolean;
  failedItemCount: number;
  evidenceItemCount: number;
}): void {
  capture(AnalyticsEvent.CHECKLIST_COMPLETED, {
    checklist_instance_id: params.checklistInstanceId,
    checklist_template_id: params.checklistTemplateId,
    completed_on_time: params.completedOnTime,
    completion_duration_seconds: params.completionDurationSeconds,
    completed_offline: params.completedOffline,
    failed_item_count: params.failedItemCount,
    evidence_item_count: params.evidenceItemCount,
  });
}

/**
 * Track checklist marked as missed (usually server-side, but can be called from client)
 */
export function trackChecklistMarkedMissed(params: {
  checklistInstanceId: string;
  checklistTemplateId: string;
  roleExpectedToComplete: string;
}): void {
  capture(AnalyticsEvent.CHECKLIST_MARKED_MISSED, {
    checklist_instance_id: params.checklistInstanceId,
    checklist_template_id: params.checklistTemplateId,
    role_expected_to_complete: params.roleExpectedToComplete,
  });
}

/**
 * Track checklist reviewed (manager viewing completed/missed checklist)
 */
export function trackChecklistReviewed(params: {
  checklistInstanceId: string;
  hadFailedItems: boolean;
  evidenceCount: number;
}): void {
  capture(AnalyticsEvent.CHECKLIST_REVIEWED, {
    checklist_instance_id: params.checklistInstanceId,
    had_failed_items: params.hadFailedItems,
    evidence_count: params.evidenceCount,
  });
}

// ============================================
// EVIDENCE EVENTS
// ============================================

/**
 * Track evidence added to checklist item
 */
export function trackEvidenceAdded(params: {
  checklistInstanceId: string;
  evidenceType: "photo" | "comment" | "both";
  associatedWithFailedItem: boolean;
}): void {
  capture(AnalyticsEvent.EVIDENCE_ADDED, {
    checklist_instance_id: params.checklistInstanceId,
    evidence_type: params.evidenceType,
    associated_with_failed_item: params.associatedWithFailedItem,
  });
}

// ============================================
// OFFLINE/SYNC EVENTS
// ============================================

/**
 * Track offline mode entered
 */
export function trackOfflineModeEntered(): void {
  capture(AnalyticsEvent.OFFLINE_MODE_ENTERED, {
    device_type: getDeviceType(),
  });
}

/**
 * Track sync completed successfully
 */
export function trackSyncCompleted(params: {
  syncedEventCount: number;
  syncDurationSeconds: number;
  hadErrors: boolean;
}): void {
  capture(AnalyticsEvent.SYNC_COMPLETED, {
    synced_event_count: params.syncedEventCount,
    sync_duration_seconds: params.syncDurationSeconds,
    had_errors: params.hadErrors,
  });
}

/**
 * Track sync failed
 */
export function trackSyncFailed(params: {
  errorType: string;
  retryCount: number;
}): void {
  capture(AnalyticsEvent.SYNC_FAILED, {
    error_type: params.errorType,
    retry_count: params.retryCount,
  });
}
